import { ScheduleItemV2 } from '@hopin-team/api-hopin';
import { Badge } from '@hopin-team/ui-badge';
import { Box } from '@hopin-team/ui-box';
import { Text } from '@hopin-team/ui-text';
import { format, parseISO } from 'date-fns';
import React from 'react';

import { getLocalisedDateTimeLabels } from '../../helpers';
import { useDateFormats, useDateLocale, useLocales } from '../../hooks';
import { DayIncrement, TimeSplitter } from '../Item';

export const OverlappingSegments = ({
  segmentIds,
  scheduleItems,
}: {
  segmentIds: string[];
  scheduleItems: ScheduleItemV2[];
}) => {
  const { t } = useLocales('reception');
  const locale = useDateLocale(navigator.language);
  const { getFormattedTime } = useDateFormats();

  if (!segmentIds.length) return null;

  const segmentsToDisplay = segmentIds.slice(0, Math.min(2, segmentIds.length));

  return (
    <>
      {segmentsToDisplay.map(segmentId => {
        const scheduleItem = scheduleItems.find(
          s => s.external_id == segmentId,
        );

        if (!scheduleItem) return undefined;

        const date = parseISO(scheduleItem.time_start);
        const dateIgnoringTimezone = new Date(
          date.valueOf() + date.getTimezoneOffset() * 60 * 1000,
        );
        const d = format(dateIgnoringTimezone, 'LLLL dd', {
          locale,
        });

        const {
          startDateTimeLabel,
          startTimeLabel,
          endTimeLabel,
          endTimeDaysIncrement,
        } = getLocalisedDateTimeLabels(scheduleItem, locale, getFormattedTime);

        return (
          <Box
            key={`${segmentId}`}
            border="grey-300"
            cornerRadius="xl"
            py={1}
            px={1.5}
            mb={1.5}
          >
            <Text pattern="captionMedium" element="p">
              {d},{' '}
              <time
                dateTime={scheduleItem.time_start}
                data-local="time"
                data-format="%L:%M%p"
                title={startDateTimeLabel}
                data-localized=""
                aria-label={startTimeLabel}
              >
                {startTimeLabel}
              </time>
              <TimeSplitter>-</TimeSplitter>
              <time
                dateTime={scheduleItem.time_end}
                data-local="time"
                data-format="%l:%M%p"
                title={startDateTimeLabel}
                data-localized=""
                aria-label={endTimeLabel}
              >
                {endTimeLabel}{' '}
                {endTimeDaysIncrement ? (
                  <DayIncrement pattern="headingSix">
                    +{endTimeDaysIncrement}
                  </DayIncrement>
                ) : (
                  ''
                )}
              </time>
            </Text>
            <Text pattern="body" element="p" color="grey-800" my={0.5}>
              {scheduleItem.name}
            </Text>
            <Badge
              label={t('seat-booked')}
              colorPattern="success"
              priority="low"
              leadingIcon="checkmark"
            />
          </Box>
        );
      })}
      {segmentIds.length > 2 && (
        <Text
          pattern="emphasis"
          element="p"
          mb={1.5}
          mt={-0.5}
          color="grey-medium"
        >
          {t('and-remaining-more', {
            remaining: segmentIds.length - segmentsToDisplay.length,
          })}
        </Text>
      )}
      <Text element="p" pattern="body" color="grey-600" mb={2}>
        {t('do-you-want-to-switch')}
      </Text>
    </>
  );
};
